'use client';

import { useCallback, useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import type { GameConfig } from '../lib/game-config';
import type { HeroData } from '../lib/heroes/types';
import { heroToDirectoryEntry } from '../lib/heroes/directory-entry';
import type { HeroDirectoryEntry } from '../lib/heroes/directory-types';
import { resolveHeroRouteId } from '../lib/heroes/routes';
import { shareAsync } from '../lib/share-async';
import HeroDetailRenderer from './hero-details/registry';
import './legacy-shell-overrides.css';

const DARK_SHELL_GAMES = new Set(['arcanatactics', 'rellion']);
const heroDetailInflight = new Map<string, Promise<HeroData>>();
const catalogInflight = new Map<string, Promise<GameHeroCatalog>>();

/** 会话内常驻：列表一次拉齐后，切换角色不再打 catalog-heroes */
type GameHeroCatalog = {
  directory: HeroDirectoryEntry[];
  heroesById: Map<string, HeroData>;
};
const catalogCache = new Map<string, GameHeroCatalog>();

type LoadState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | {
      status: 'ready';
      hero: HeroData;
      directory: HeroDirectoryEntry[];
    };

function heroFromCatalog(
  catalog: GameHeroCatalog,
  gameKey: string,
  routeId: string,
) {
  const heroId =
    resolveHeroRouteId(gameKey, routeId, catalog.directory) || routeId;
  return catalog.heroesById.get(heroId) || null;
}

async function fetchHeroDetail(gameKey: string, routeId: string) {
  return shareAsync(heroDetailInflight, `${gameKey}:${routeId}`, async () => {
    const detailRes = await fetch(
      `/api/games/${encodeURIComponent(gameKey)}/catalog-heroes/${encodeURIComponent(routeId)}`,
      { cache: 'no-store', credentials: 'same-origin' },
    );

    if (detailRes.ok) {
      const hero = (await detailRes.json()) as HeroData | null;
      if (!hero?.id) throw new Error('英雄不存在或已下线');
      return hero;
    }

    if (detailRes.status === 404) {
      const catalog = await loadHeroCatalog(gameKey);
      const heroId = resolveHeroRouteId(gameKey, routeId, catalog.directory);
      if (!heroId) throw new Error('英雄不存在或已下线');
      const cached = catalog.heroesById.get(heroId);
      if (cached) return cached;
      const retry = await fetch(
        `/api/games/${encodeURIComponent(gameKey)}/catalog-heroes/${encodeURIComponent(heroId)}`,
        { cache: 'no-store', credentials: 'same-origin' },
      );
      if (!retry.ok) throw new Error('英雄不存在或已下线');
      const hero = (await retry.json()) as HeroData | null;
      if (!hero?.id) throw new Error('英雄不存在或已下线');
      return hero;
    }

    throw new Error('英雄加载失败');
  });
}

async function loadHeroCatalog(gameKey: string) {
  const hit = catalogCache.get(gameKey);
  if (hit) return hit;

  return shareAsync(catalogInflight, gameKey, async () => {
    const listRes = await fetch(
      `/api/games/${encodeURIComponent(gameKey)}/catalog-heroes`,
      { cache: 'no-store', credentials: 'same-origin' },
    );
    if (!listRes.ok) throw new Error('英雄列表加载失败');
    const listPayload = (await listRes.json()) as { heroes?: HeroData[] };
    const heroes = Array.isArray(listPayload.heroes) ? listPayload.heroes : [];
    const heroesById = new Map<string, HeroData>();
    const directory = heroes.map((hero) => {
      heroesById.set(hero.id, hero);
      return heroToDirectoryEntry(hero);
    });
    const catalog: GameHeroCatalog = { directory, heroesById };
    catalogCache.set(gameKey, catalog);
    return catalog;
  }, 60_000);
}

/**
 * 真实用户英雄详情：首屏拉齐角色列表到会话缓存；
 * 之后列表内切换优先走缓存，不再重复请求 catalog-heroes。
 */
export default function HeroDetailClient({
  game,
  routeId,
  startAtContent,
  initialContentSection,
  initialSectionOffset,
}: {
  game: GameConfig;
  routeId: string;
  startAtContent?: boolean;
  initialContentSection?: 'skills' | 'archive';
  initialSectionOffset?: number;
}) {
  const router = useRouter();
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [directory, setDirectory] = useState<HeroDirectoryEntry[]>(() => {
    return catalogCache.get(game.key)?.directory || [];
  });

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      // 切换角色时保留当前画面，避免整页闪白
      setState((previous) =>
        previous.status === 'ready' ? previous : { status: 'loading' },
      );

      try {
        const cachedCatalog = catalogCache.get(game.key);
        if (cachedCatalog) {
          setDirectory(cachedCatalog.directory);
          const cachedHero = heroFromCatalog(cachedCatalog, game.key, routeId);
          if (cachedHero) {
            if (cancelled) return;
            document.title = `${cachedHero.name}｜${game.name} Wiki`;
            setState({
              status: 'ready',
              hero: cachedHero,
              directory: cachedCatalog.directory,
            });
            return;
          }
        }

        // 首屏：详情与列表并行；列表写入会话缓存供后续秒切
        const catalogPromise = loadHeroCatalog(game.key).catch(() => null);
        const hero = await fetchHeroDetail(game.key, routeId);
        if (cancelled) return;

        const catalog = (await catalogPromise) || catalogCache.get(game.key);
        if (cancelled) return;

        const fromCatalog = catalog
          ? heroFromCatalog(catalog, game.key, routeId)
          : null;
        const nextHero = fromCatalog || hero;
        const nextDirectory =
          catalog && catalog.directory.length > 0
            ? catalog.directory
            : [heroToDirectoryEntry(nextHero)];

        document.title = `${nextHero.name}｜${game.name} Wiki`;
        setDirectory(nextDirectory);
        setState({
          status: 'ready',
          hero: nextHero,
          directory: nextDirectory,
        });
      } catch (error) {
        if (!cancelled) {
          setState({
            status: 'error',
            message: error instanceof Error ? error.message : '英雄加载失败',
          });
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [game.key, game.name, routeId]);

  const onNavigateHero = useCallback(
    (href: string) => {
      router.push(href);
    },
    [router],
  );

  if (state.status === 'error') {
    return (
      <main className="v2-shell" role="alert">
        <div style={{ padding: '48px 24px', textAlign: 'center' }}>
          <h1>加载失败</h1>
          <p>{state.message}</p>
          <p>
            <a href={`/${game.domain}/dcatalog`}>返回图鉴</a>
            {' · '}
            <a href={`/${game.domain}`}>回首页</a>
          </p>
        </div>
      </main>
    );
  }

  if (state.status === 'loading') {
    return (
      <div
        className={
          DARK_SHELL_GAMES.has(game.key) ? 'hero-detail-pending-shell' : undefined
        }
        aria-busy="true"
        data-hero-detail-pending="1"
      />
    );
  }

  const readyDirectory =
    directory.length > 1 ? directory : state.directory;

  return (
    <HeroDetailRenderer
      key={`${state.hero.gameId}:${state.hero.id}`}
      game={game}
      hero={state.hero}
      directory={readyDirectory}
      startAtContent={startAtContent}
      initialContentSection={initialContentSection}
      initialSectionOffset={initialSectionOffset}
      onNavigateHero={onNavigateHero}
    />
  );
}
